import { computed, inject, Injectable, Injector, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ApplicationProductsService } from '@services/api-calls/application-products.service';
import { ApplicationService } from '@services/api-calls/application.service';
import { BeneficiaryService } from '@services/api-calls/beneficiary.service';
import { ExistingInsuranceService } from '@services/api-calls/existing-insurance.service';
import { InsuredClientService } from '@services/api-calls/insured-client.service';
import { PaymentFormPopulationService } from '@services/api-calls/payment-form-population.service';
import { ApplicationStateService } from '@services/application-state.service';
import { FormRegistryService } from '@services/form/form-registry.service';
import { StepperService } from '@services/layout/stepper.service';
import { SubstepSaveFunctionService } from '@services/substep-save-function.service';

@Injectable({
    providedIn: 'root',
})
export class UnsavedChangesService {
    private readonly stepperService = inject(StepperService);
    private readonly applicationStateService = inject(ApplicationStateService);
    private readonly applicationProductsService = inject(ApplicationProductsService);
    private readonly applicationService = inject(ApplicationService);
    private readonly formRegistry = inject(FormRegistryService);
    private readonly router = inject(Router);
    private readonly injector = inject(Injector);

    private _substepSaveFunctionService: SubstepSaveFunctionService | null = null;

    readonly showModal = signal(false);
    readonly pendingNavigationUrl = signal<string | null>(null);

    readonly currentFormDirty = computed(() => {
        if (this.applicationStateService.isCompleted()) return false;
        if (this.applicationProductsService.isDeclined()) return false;

        const stepIndex = this.stepperService.currentStepIndex();
        const subStepIndex = this.stepperService.currentSubStepIndex();
        if (stepIndex === -1 || subStepIndex === -1) return false;

        const steps = this.stepperService.steps();
        const currentStep = steps[stepIndex];
        const currentSubStep = currentStep?.subSteps[subStepIndex];
        if (!currentStep || !currentSubStep) return false;

        const formGroup = this.formRegistry.getFormGroup(currentStep.step, currentSubStep.step);
        if (!formGroup || formGroup.disabled) return false;

        this.formRegistry.getSubstepValue([currentStep.step, currentSubStep.step])?.();
        return formGroup.dirty;
    });

    private getSubstepSaveFunctionService(): SubstepSaveFunctionService {
        if (!this._substepSaveFunctionService) {
            this._substepSaveFunctionService = this.injector.get(SubstepSaveFunctionService);
        }
        return this._substepSaveFunctionService;
    }

    hasUnsavedChanges(): boolean {
        if (this.applicationStateService.isCompleted()) return false;
        if (this.applicationProductsService.isDeclined()) return false;

        const stepIndex = this.stepperService.currentStepIndex();
        const subStepIndex = this.stepperService.currentSubStepIndex();

        if (stepIndex === -1 || subStepIndex === -1) return false;

        const steps = this.stepperService.steps();
        const currentStep = steps[stepIndex];
        const currentSubStep = currentStep?.subSteps[subStepIndex];

        if (!currentStep || !currentSubStep) return false;

        const formGroup = this.formRegistry.getFormGroup(currentStep.step, currentSubStep.step);
        if (!formGroup || formGroup.disabled) return false;

        return this.getSubstepSaveFunctionService().hasChanges(currentStep.step, currentSubStep.step);
    }

    requestNavigation(url: string): boolean {
        if (this.router.url.split('?')[0] === url.split('?')[0]) return false;
        const hasChanges = this.hasUnsavedChanges();
        if (!hasChanges) return false;
        this.pendingNavigationUrl.set(url);
        this.showModal.set(true);
        return true;
    }

    confirmDiscard(): void {
        const url = this.pendingNavigationUrl();
        this.showModal.set(false);
        this.pendingNavigationUrl.set(null);

        this.applicationService.refreshAndWait().subscribe({
            next: () => {
                this.repatchFormsFromServerData();
                if (url) {
                    this.router.navigateByUrl(url);
                }
            },
            error: () => {
                if (url) {
                    this.router.navigateByUrl(url);
                }
            },
        });
    }

    private repatchFormsFromServerData(): void {
        const insuredClientService = this.injector.get(InsuredClientService);
        const beneficiaryService = this.injector.get(BeneficiaryService);
        const existingInsuranceService = this.injector.get(ExistingInsuranceService);
        const paymentFormPopulationService = this.injector.get(PaymentFormPopulationService);

        const insuredClient = this.applicationService.applicationInsuredClient();
        if (insuredClient) {
            insuredClientService.patchInsuredClientDataOnForm(insuredClient);
        }
        beneficiaryService.patchBeneficiariesDataOnForm();
        existingInsuranceService.patchInsuranceCompaniesDataOnForm();
        this.applicationService.patchApplicationDataOnForm();
        this.applicationProductsService.repatchProductSelectionFromServer();
        paymentFormPopulationService.repatchPaymentDataFromServer();
    }

    confirmStay(): void {
        this.showModal.set(false);
        this.pendingNavigationUrl.set(null);
    }
}
